import cn from 'classnames'
import { Ops } from '@willie/schemas'
import type { BadgeProps } from '@radix-ui/themes'
import { type Accept } from 'react-dropzone'
import { HEIC_ACCEPT } from '~/features/installations/heic'

export const IMAGE_ACCEPT: Accept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif']
}

// What a dropzone accepts. Wider than IMAGE_ACCEPT: HEIC is converted to JPEG before
// upload, so it is droppable but never storable.
export const IMAGE_DROP_ACCEPT: Accept = { ...IMAGE_ACCEPT, ...HEIC_ACCEPT }

export const IMAGE_MAX_BYTES = 10 * 1024 * 1024

export const PENDING_FILTER = Ops.StationInstallations.PENDING_FILTER
export const COMPATIBILITY_STATUS_OPTIONS = Ops.StationInstallations.compatibilityStatusSchema.options
export const STAGE_OPTIONS = Ops.StationInstallations.stageSchema.options
export const STATION_TYPE_OPTIONS = Ops.StationInstallations.stationTypeSchema.options

// A batch is assembled over minutes and a site's stations do not change under it, so the wizard's
// one-query-per-site reads are not repeated on every mount or window focus.
export const SITE_STATIONS_STALE_MS = 5 * 60 * 1000
export const LOGGER_MAKER_OPTIONS = Ops.StationInstallations.loggerMakerSchema.options
export const STALLED_BECAUSE_OF_OPTIONS = Ops.StationInstallations.stalledBecauseOfSchema.options

// Stored verbatim in retirement_reason, so renaming one splits it from the rows already written.
export const RETIREMENT_REASON_PRESETS = [
  'Incompatible',
  'Meter broken',
  'Material meter change',
  'Material lost',
  'Cancelled'
] as const
export const INSTALLATION_PROFILE_OPTIONS = Ops.InstallationProfiles.INSTALLATION_PROFILE_SLUGS

export type MeterCompatibility = Ops.StationInstallations.MeterCompatibility
export type CompatibilityStatus = Ops.StationInstallations.CompatibilityStatus
export type Stage = Ops.StationInstallations.Stage
export type StationType = Ops.StationInstallations.StationType
export type MeterMaker = Ops.StationInstallations.MeterMaker
export type LoggerMaker = Ops.StationInstallations.LoggerMaker
export type InstallationProfile = Ops.InstallationProfiles.InstallationProfile
export type StalledBecauseOf = Ops.StationInstallations.StalledBecauseOf
export type InstallationEventType = Ops.StationInstallations.InstallationEventType

export const meterCompatibilityLabel: Record<MeterCompatibility, string> = {
  compatible: 'Compatible',
  in_analysis: 'In analysis',
  not_compatible: 'Not compatible'
}

export const compatibilityStatusLabel: Record<CompatibilityStatus, string> = {
  pending: 'Pending',
  in_analysis: 'In analysis',
  not_compatible: 'Not compatible',
  compatible_supported: 'Compatible',
  compatible_approved: 'Compatible · approved',
  compatible_not_supported: 'Compatible · not supported'
}

export const compatibilityStatusColor: Record<CompatibilityStatus, BadgeProps['color']> = {
  pending: 'gray',
  in_analysis: 'amber',
  not_compatible: 'red',
  compatible_supported: 'green',
  compatible_approved: 'teal',
  compatible_not_supported: 'orange'
}

// A clickable installation row: tinted when stalled, dimmed when retired.
export const installationRowClass = ({
  isStalled,
  isRetired
}: {
  isStalled: boolean
  isRetired: boolean
}): string =>
  cn('cursor-pointer', {
    // Retired wins: an installation can still carry an unresolved stall, and two hover
    // backgrounds on one row resolve by stylesheet order rather than by which is meant.
    'text-[var(--gray-11)] hover:bg-[var(--gray-a2)]': isRetired,
    'bg-[var(--red-a3)] hover:bg-[var(--red-a4)]': !isRetired && isStalled,
    'hover:bg-[var(--gray-a2)]': !isRetired && !isStalled
  })

export const compatibilityStatusBarBgClass: Record<CompatibilityStatus, string> = {
  pending: 'bg-[var(--gray-9)]',
  in_analysis: 'bg-[var(--amber-9)]',
  not_compatible: 'bg-[var(--red-9)]',
  compatible_supported: 'bg-[var(--green-9)]',
  compatible_approved: 'bg-[var(--teal-9)]',
  compatible_not_supported: 'bg-[var(--orange-9)]'
}

export const stageLabel: Record<Stage, string> = {
  install_created: 'Install created',
  image_received: 'Image received',
  compatibility_checked: 'Compatibility checked',
  order_placed: 'Order placed',
  hardware_shipped: 'Hardware shipped',
  station_installed: 'Station installed',
  index_aligned: 'Index aligned'
}

export const stageColor: Record<Stage, BadgeProps['color']> = {
  install_created: 'gray',
  image_received: 'blue',
  compatibility_checked: 'violet',
  order_placed: 'cyan',
  hardware_shipped: 'indigo',
  station_installed: 'green',
  index_aligned: 'teal'
}

export const stageBarBgClass: Record<Stage, string> = {
  install_created: 'bg-[var(--gray-9)]',
  image_received: 'bg-[var(--blue-9)]',
  compatibility_checked: 'bg-[var(--violet-9)]',
  order_placed: 'bg-[var(--cyan-9)]',
  hardware_shipped: 'bg-[var(--indigo-9)]',
  station_installed: 'bg-[var(--green-9)]',
  index_aligned: 'bg-[var(--teal-9)]'
}

export const loggerMakerLabel: Record<LoggerMaker, string> = {
  shayp: 'Shayp',
  efento: 'Efento',
  adeunis: 'Adeunis',
  other: 'Other'
}

export const stalledBecauseOfLabel: Record<StalledBecauseOf, string> = {
  client: 'The client',
  logger_provider: 'The logger provider',
  willie: 'Willie',
  other: 'Other'
}

// A Record over the union, so adding an event type fails type-check here rather than rendering
// under whichever label the timeline's fallback branch happened to reach.
export const installationEventLabel: Record<InstallationEventType, string> = {
  stalled: 'Marked as stalled',
  reminder_sent: 'Reminder sent',
  guide_opened: 'Opened the guide',
  help_requested: 'Asked for help',
  serial_mismatch: 'Scanned a module from another site',
  install_submitted: 'Submitted the installation',
  module_assigned: 'Chose this station for a module',
  install_verification_reported: 'Data verification reported',
  outbound_email: 'Outbound Email',
  index_capture_requested: 'Index capture requested',
  index_capture_received: 'Index capture received',
  index_capture_decided: 'Index capture decided',
  index_aligned: 'Marked as index aligned',
  retired: 'Installation retired',
  transactional_sent: 'Email sent',
  material_return_sent: 'Material return label sent',
  material_return_received: 'Returned material received',
  compatibility_confirmed: 'Confirmed meter compatibility',
  unsupported_profile_approved: 'Approved an unsupported profile'
}

// serial_mismatch means the boxes are swapped right now, while someone is still on site — the one
// assistant event worth interrupting an operator for.
export const INSTALLATION_EVENT_TONE: Record<InstallationEventType, 'red' | 'gray'> = {
  stalled: 'red',
  serial_mismatch: 'red',
  reminder_sent: 'gray',
  guide_opened: 'gray',
  help_requested: 'gray',
  install_submitted: 'gray',
  module_assigned: 'gray',
  install_verification_reported: 'gray',
  outbound_email: 'gray',
  index_capture_requested: 'gray',
  index_capture_received: 'gray',
  index_capture_decided: 'gray',
  index_aligned: 'gray',
  retired: 'gray',
  transactional_sent: 'gray',
  material_return_sent: 'gray',
  material_return_received: 'gray',
  compatibility_confirmed: 'gray',
  unsupported_profile_approved: 'gray'
}
